import React, {
    memo,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button, Icon, Loader, Modal, Tabs, Tooltip } from '@Sage/hoi-poi-ui';
import { COMPANIES, CONTACTS } from 'constants/Entities';
import { EntityFiltersActions, EntityListSelectActions } from 'actions';
import Context from 'managers/Context';
import { EntitiesSelectionService } from 'services';
import { getLiteral } from 'utils/getLiteral';
import { logMarketingCampaignsEvent } from '../../../utils/marketingTracking';
import RecipientsAccountsTab from './RecipientsAccountsTab';
import RecipientsContactsTab from './RecipientsContactsTab';
import SelectionTotalsSummary from './SelectionTotalsSummary';
import EmailsRemainingIndicator from './EmailsRemainingIndicator';
import { useMarketingCampaignLimits } from '../../../hooks/useMarketingCampaignLimits';

import './styles.scss';

const TAB_ACCOUNTS = 'accounts';
const TAB_CONTACTS = 'contacts';

const INITIAL_MARKETING_STATUS_STATE = {
    [TAB_ACCOUNTS]: false,
    [TAB_CONTACTS]: false,
};

const MARKETING_SELECTION_FOR = 'marketingCampaigns';

const SelectRecipientsModal = memo(
    ({
        isOpen,
        campaignId,
        token: initialToken,
        onDone,
        onCancel,
        companiesToken,
        contactsToken,
        cancelSelection,
        accountsTotal,
        contactsTotal,
        getSelectionInfo,
        setActiveEntity,
        resetListSelect,
        syncEntityListSelectionToken,
        clearFilters,
        hideFilters,
    }) => {
        const [selectionToken, setSelectionToken] = useState(initialToken || null);
        const [selectionReadyForList, setSelectionReadyForList] = useState(false);
        const [activeTab, setActiveTab] = useState(TAB_ACCOUNTS);
        const [isDoneSubmitting, setIsDoneSubmitting] = useState(false);
        const [initialMarketingStatusApplied, setInitialMarketingStatusApplied] = useState(
            INITIAL_MARKETING_STATUS_STATE,
        );

        const isMountedRef = useRef(false);
        const selectionTokenRef = useRef('');
        const committedTokenRef = useRef('');
        const prevIsOpenRef = useRef(false);
        const entitySelectionRequestRef = useRef(0);
        const initialCountsLoadedRef = useRef(false);

        useEffect(() => {
            isMountedRef.current = true;
            return () => {
                isMountedRef.current = false;
            };
        }, []);

        useLayoutEffect(() => {
            if (!isOpen) return;

            resetListSelect(COMPANIES, false);
            resetListSelect(CONTACTS, false);
            setActiveTab(TAB_ACCOUNTS);
            setActiveEntity(COMPANIES);
            clearFilters({
                entity: COMPANIES,
                filterKey: 'recipientsAccounts',
                isAPurge: false,
                refresh: false,
            });
            clearFilters({
                entity: CONTACTS,
                filterKey: 'recipientsContacts',
                isAPurge: false,
                refresh: false,
            });
            hideFilters(COMPANIES, 'recipientsAccounts');
            hideFilters(CONTACTS, 'recipientsContacts');
        }, [isOpen, resetListSelect, setActiveEntity, clearFilters, hideFilters]);

        useEffect(() => {
            if (!isOpen) {
                prevIsOpenRef.current = false;
                resetListSelect(COMPANIES, false);
                resetListSelect(CONTACTS, false);
                setActiveTab(TAB_ACCOUNTS);
                setSelectionReadyForList(false);
                setInitialMarketingStatusApplied(INITIAL_MARKETING_STATUS_STATE);
                entitySelectionRequestRef.current += 1;
                initialCountsLoadedRef.current = false;
                return;
            }

            const justOpened = !prevIsOpenRef.current;
            prevIsOpenRef.current = true;

            if (justOpened) {
                committedTokenRef.current = initialToken || '';
                selectionTokenRef.current = '';
            } else {
                // Tab switches remount list tabs; token-only sync must not re-handshake here.
                return;
            }

            setSelectionReadyForList(false);

            const entityConfig = COMPANIES;
            const entity = entityConfig.trueName;
            const requestId = ++entitySelectionRequestRef.current;

            const prepareModalToken = async () => {
                if (!justOpened) {
                    return selectionTokenRef.current;
                }

                const sourceToken = committedTokenRef.current;
                if (!sourceToken) {
                    return '';
                }

                try {
                    const cloneResult = await EntitiesSelectionService.cloneSelectionToken(
                        sourceToken,
                        MARKETING_SELECTION_FOR,
                    );
                    return cloneResult?.token || cloneResult?.Token || '';
                } catch {
                    return '';
                }
            };

            prepareModalToken()
                .then((modalToken) => {
                    if (!isMountedRef.current || requestId !== entitySelectionRequestRef.current) {
                        return null;
                    }

                    if (justOpened) {
                        selectionTokenRef.current = modalToken;
                        setSelectionToken(modalToken);
                    }

                    const tokenForSelection =
                        selectionTokenRef.current || modalToken || committedTokenRef.current;

                    return Context.domainManager.sendEntityListSelection({
                        selectionForId: campaignId ? Number(campaignId) : 0,
                        selectionFor: MARKETING_SELECTION_FOR,
                        entity,
                        token: tokenForSelection,
                    });
                })
                .then((data) => {
                    if (!data) return;
                    if (!isMountedRef.current || requestId !== entitySelectionRequestRef.current) {
                        return;
                    }
                    const token = data?.token || data?.Token || '';
                    selectionTokenRef.current = token;
                    setSelectionToken(token);
                    syncEntityListSelectionToken({
                        entity: entityConfig,
                        data,
                        selectionFor: MARKETING_SELECTION_FOR,
                        selectionEntity: entity,
                    });
                })
                .catch(() => {
                    // proceed with empty token — user can still make a selection
                })
                .finally(() => {
                    if (!isMountedRef.current || requestId !== entitySelectionRequestRef.current) {
                        return;
                    }
                    setSelectionReadyForList(true);
                });
        }, [isOpen, campaignId, initialToken, resetListSelect, syncEntityListSelectionToken]);

        const loadInitialSelectionCounts = useCallback(() => {
            const token = selectionTokenRef.current || selectionToken || initialToken || '';

            if (!token) return;

            getSelectionInfo({
                entity: COMPANIES,
                token: companiesToken || token,
                selectionFor: MARKETING_SELECTION_FOR,
                selectionEntity: 'accounts',
            });
            getSelectionInfo({
                entity: CONTACTS,
                token: contactsToken || token,
                selectionFor: MARKETING_SELECTION_FOR,
                selectionEntity: 'contacts',
            });
        }, [companiesToken, contactsToken, getSelectionInfo, initialToken, selectionToken]);

        useEffect(() => {
            if (!isOpen || !selectionReadyForList || initialCountsLoadedRef.current) return;

            initialCountsLoadedRef.current = true;
            loadInitialSelectionCounts();
        }, [isOpen, selectionReadyForList, loadInitialSelectionCounts]);

        const handleTabChange = useCallback(
            (key) => {
                setActiveTab(key);
                setActiveEntity(key === TAB_CONTACTS ? CONTACTS : COMPANIES);
            },
            [setActiveEntity],
        );

        const handleInitialMarketingStatusApplied = useCallback((tabKey) => {
            setInitialMarketingStatusApplied((prev) => {
                if (prev[tabKey]) {
                    return prev;
                }

                return {
                    ...prev,
                    [tabKey]: true,
                };
            });
        }, []);

        const handleDone = useCallback(() => {
            const baseToken = selectionTokenRef.current || selectionToken || initialToken || '';
            const campaignToken = companiesToken || contactsToken || baseToken;

            if (!campaignToken) {
                onDone?.('', 0);
                return;
            }

            setIsDoneSubmitting(true);

            try {
                const nextCount = (Number(accountsTotal) || 0) + (Number(contactsTotal) || 0);

                selectionTokenRef.current = campaignToken;
                committedTokenRef.current = campaignToken;
                setSelectionToken(campaignToken);
                logMarketingCampaignsEvent({ submodule: 'list', functionality: 'selectList' });
                onDone?.(campaignToken, nextCount);
            } finally {
                if (isMountedRef.current) {
                    setIsDoneSubmitting(false);
                }
            }
        }, [
            accountsTotal,
            companiesToken,
            contactsToken,
            contactsTotal,
            initialToken,
            onDone,
            selectionToken,
        ]);

        const restoreCommittedSelectionInRedux = useCallback(() => {
            const committed = committedTokenRef.current;
            if (!committed) {
                resetListSelect(COMPANIES, false);
                resetListSelect(CONTACTS, false);
                return Promise.resolve();
            }

            const selectionForId = campaignId ? Number(campaignId) : 0;
            const entities = [COMPANIES, CONTACTS];

            return Promise.all(
                entities.map((entityConfig) =>
                    Context.domainManager
                        .sendEntityListSelection({
                            selectionForId,
                            selectionFor: 'marketingCampaigns',
                            entity: entityConfig.trueName,
                            token: committed,
                        })
                        .then((data) => {
                            syncEntityListSelectionToken({
                                entity: entityConfig,
                                data,
                                selectionFor: 'marketingCampaigns',
                                selectionEntity: entityConfig.trueName,
                            });
                        })
                        .catch(() => null),
                ),
            );
        }, [campaignId, resetListSelect, syncEntityListSelectionToken]);

        const handleCancel = useCallback(() => {
            cancelSelection();
            restoreCommittedSelectionInRedux().finally(() => {
                onCancel?.();
            });
        }, [cancelSelection, onCancel, restoreCommittedSelectionInRedux]);

        const isDoneDisabled = isDoneSubmitting;
        const isSelectionSummaryLoading = !selectionReadyForList;
        const selectedRecipientsCount = (Number(accountsTotal) || 0) + (Number(contactsTotal) || 0);
        const { data: campaignLimitsData, loading: campaignLimitsLoading } =
            useMarketingCampaignLimits({ enabled: isOpen });

        const tabs = useMemo(() => {
            const tabIconTitle = (icon, label, isActive) => (
                <Tooltip content={label} placement="top">
                    <div
                        className={`fm-select-recipients-modal__tab-title${isActive ? ' fm-select-recipients-modal__tab-title--active' : ''}`}
                        aria-label={label}
                    >
                        <Icon
                            name={icon}
                            size="large"
                            className="fm-select-recipients-modal__tab-icon"
                        />
                    </div>
                </Tooltip>
            );

            const tabLoader = (
                <div className="fm-select-recipients-modal__tab-loader">
                    <Loader />
                </div>
            );

            const accountsTabContent = selectionReadyForList ? (
                <RecipientsAccountsTab
                    selectionToken={selectionToken}
                    shouldApplyInitialMarketingStatus={!initialMarketingStatusApplied[TAB_ACCOUNTS]}
                    onInitialMarketingStatusApplied={handleInitialMarketingStatusApplied}
                />
            ) : (
                tabLoader
            );

            const contactsTabContent = selectionReadyForList ? (
                <RecipientsContactsTab
                    selectionToken={selectionToken}
                    shouldApplyInitialMarketingStatus={!initialMarketingStatusApplied[TAB_CONTACTS]}
                    onInitialMarketingStatusApplied={handleInitialMarketingStatusApplied}
                />
            ) : (
                tabLoader
            );

            return [
                {
                    key: TAB_ACCOUNTS,
                    title: tabIconTitle(
                        'accounts',
                        getLiteral('label_accounts'),
                        activeTab === TAB_ACCOUNTS,
                    ),
                    content: accountsTabContent,
                },
                {
                    key: TAB_CONTACTS,
                    title: tabIconTitle(
                        'contacts',
                        getLiteral('label_contacts'),
                        activeTab === TAB_CONTACTS,
                    ),
                    content: contactsTabContent,
                },
            ];
        }, [
            activeTab,
            handleInitialMarketingStatusApplied,
            initialMarketingStatusApplied,
            selectionToken,
            selectionReadyForList,
        ]);

        if (!isOpen) return null;

        return (
            <Modal
                className="fm-select-recipients-modal"
                isOpen={isOpen}
                size="full"
                useContentStaticHeight
                title={getLiteral('label_select_recipients')}
                onRequestClose={handleCancel}
                useCornerClose={true}
                overrides={{
                    content: {
                        className: 'fm-select-recipients-modal__content',
                    },
                }}
            >
                <Tabs
                    className="fm-select-recipients-modal__tabs"
                    tabs={tabs}
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    editable={false}
                />
                <div className="fm-select-recipients-modal__footer">
                    <SelectionTotalsSummary
                        accountsCount={accountsTotal}
                        contactsCount={contactsTotal}
                        isLoading={isSelectionSummaryLoading}
                    />
                    <div className="fm-select-recipients-modal__footer-buttons">
                        <EmailsRemainingIndicator
                            limitsData={campaignLimitsData}
                            isLoading={campaignLimitsLoading}
                            selectedCount={selectedRecipientsCount}
                        />
                        <Button type="secondary" onClick={handleCancel}>
                            {getLiteral('action_cancel')}
                        </Button>
                        <Tooltip content={undefined} placement="top">
                            <div className="fm-select-recipients-modal__done-wrapper">
                                <Button
                                    type="primary"
                                    isDisabled={isDoneDisabled}
                                    onClick={handleDone}
                                >
                                    {getLiteral('action_done')}
                                </Button>
                            </div>
                        </Tooltip>
                    </div>
                </div>
            </Modal>
        );
    },
);

SelectRecipientsModal.displayName = 'SelectRecipientsModal';

SelectRecipientsModal.propTypes = {
    isOpen: PropTypes.bool,
    campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    token: PropTypes.string,
    onDone: PropTypes.func,
    onCancel: PropTypes.func,
};

function mapStateToProps(state) {
    const companies = state.entityListSelect?.[COMPANIES.entity];
    const contacts = state.entityListSelect?.[CONTACTS.entity];
    return {
        companiesToken: companies?.token || '',
        contactsToken: contacts?.token || '',
        accountsTotal: Number(companies?.total ?? 0) || 0,
        contactsTotal: Number(contacts?.total ?? 0) || 0,
    };
}

const boundEntityListSelect = (dispatch) => bindActionCreators(EntityListSelectActions, dispatch);

const mapDispatchToProps = (dispatch) => {
    const entityListSelect = boundEntityListSelect(dispatch);
    const entityFilters = bindActionCreators(EntityFiltersActions, dispatch);
    return {
        cancelSelection: entityListSelect.cancelSelection,
        getSelectionInfo: entityListSelect.getSelectionInfo,
        setActiveEntity: entityListSelect.setActive,
        resetListSelect: entityListSelect.resetListSelect,
        syncEntityListSelectionToken: entityListSelect.syncEntityListSelectionToken,
        clearFilters: entityFilters.clearFilters,
        hideFilters: entityFilters.hideFilters,
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectRecipientsModal);
